import api from './api';
import type {
  AppelOffre,
  CreateAppelOffreRequest,
  UpdateAppelOffreRequest,
  AppelOffreFilters,
} from '../types';

export interface AoAccessReportUser {
  id: string;
  email: string;
  role?: string | null;
  userStatus: 'active' | 'banned' | 'unverified';
  bannedUntil?: string | null;
  collaborateur?: {
    id: string;
    nom: string;
    email?: string | null;
    estActif: boolean;
    service?: {
      id: string;
      nom: string;
    } | null;
  } | null;
}

export interface AoAccessReportEntry {
  permissionId?: string | null;
  permissionType: 'owner' | 'read' | 'write' | 'admin';
  accessMode: 'read' | 'write';
  source: 'owner' | 'permission';
  grantedAt?: string | null;
  grantedBy?: AoAccessReportUser | null;
  user?: AoAccessReportUser | null;
}

export interface AoAccessReportItem {
  aoId: string;
  reference: string;
  titre: string;
  statut?: string | null;
  aoManager?: string | null;
  serviceResponsable?: {
    id: string;
    nom: string;
  } | null;
  owner?: AoAccessReportUser | null;
  readCount: number;
  writeCount: number;
  totalAccessCount: number;
  accesses: AoAccessReportEntry[];
}

export const appelsOffresService = {
  async list(filters?: AppelOffreFilters): Promise<AppelOffre[]> {
    const params = new URLSearchParams();
    if (filters?.proprietaireId) params.append('proprietaireId', filters.proprietaireId);
    if (filters?.statut) params.append('statut', filters.statut);
    if (filters?.search) params.append('search', filters.search);

    const { data } = await api.get<{ success: boolean; data: { appelsOffres: AppelOffre[] } }>(
      `/appels-offres?${params.toString()}`
    );
    return data.data.appelsOffres;
  },

  async getAll(): Promise<AppelOffre[]> {
    return this.list();
  },

  async getAccessReport(filters?: { aoId?: string; search?: string }): Promise<AoAccessReportItem[]> {
    const params = new URLSearchParams();
    if (filters?.aoId) params.append('aoId', filters.aoId);
    if (filters?.search) params.append('search', filters.search);

    const query = params.toString();
    const { data } = await api.get<{ success: boolean; data: { items: AoAccessReportItem[] } }>(
      `/appels-offres/access-report${query ? `?${query}` : ''}`
    );
    return data.data.items;
  },

  async getById(id: string): Promise<AppelOffre> {
    const { data } = await api.get<{ success: boolean; data: AppelOffre }>(`/appels-offres/${id}`);
    return data.data;
  },

  async create(aoData: CreateAppelOffreRequest): Promise<AppelOffre> {
    const { data } = await api.post<{ success: boolean; data: { appelOffre: AppelOffre } }>(
      '/appels-offres',
      aoData
    );
    return data.data.appelOffre;
  },

  async update(id: string, aoData: UpdateAppelOffreRequest): Promise<AppelOffre> {
    const { data } = await api.put<{ success: boolean; data: AppelOffre }>(
      `/appels-offres/${id}`,
      aoData
    );
    return data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/appels-offres/${id}`);
  },

  // Permissions
  async getPermissions(id: string): Promise<{
    canEdit: boolean;
    canDelete: boolean;
    accessLevel: string;
    permissions?: Array<{
      id: string;
      aoId: string;
      userId: string;
      permissionType: 'read' | 'write' | 'admin';
      grantedAt: string;
    }>;
  }> {
    const { data } = await api.get<{
      success: boolean;
      data: {
        canEdit: boolean;
        canDelete: boolean;
        accessLevel: string;
        permissions?: Array<{
          id: string;
          aoId: string;
          userId: string;
          permissionType: 'read' | 'write' | 'admin';
          grantedAt: string;
        }>;
      };
    }>(
      `/appels-offres/${id}/permissions`
    );
    return data.data;
  },

  // Critères d'évaluation
  async getCriteres(id: string): Promise<any[]> {
    const { data } = await api.get<{ success: boolean; data: { criteres: any[] } }>(
      `/appels-offres/${id}/criteres`
    );
    return data.data.criteres;
  },

  async updateCriteres(id: string, criteres: any[]): Promise<void> {
    await api.put(`/appels-offres/${id}/criteres`, { criteres });
  },
};

export default appelsOffresService;
